<template>
  <MainLayout>
    <BackButton text="back to projects list" :route="$routes.projects()" />
    <v-container max-width="1280">
      <h2 class="text-center mb-12">Project Comparison   </h2>
      <v-row>
        <v-col>
          <v-alert icon="fas fa-info-circle" closable color="grey" class="ml-5 mr-5 mb-5">
              The comparison that takes place in this page, is taking in mind ONLY
              the common <b>Aggregation Levels</b> ({{
                commonAggregations.join(", ")
              }}), <b>Dimensions</b> ({{ commonDimensions.join(", ") }}) and
              <b>Sectors</b> ({{ commonSectors.join(", ") }}) of the selected
              projects
          </v-alert>
          <v-tabs v-model="tab" bg-color="primary">
            <v-tab style="text-transform: none" value="one">KPIs</v-tab>
            <v-tab style="text-transform: none" value="two">PSIs</v-tab>
          </v-tabs>
          <v-window v-model="tab">
            <v-list-group class="mb-2" v-if="errors">
              <v-list-item variant="flat" class="danger">
                {{ errors }}
              </v-list-item>
            </v-list-group>
            <v-window-item value="one"
                           reverse-transition="fade-transition"
                           transition="false"
            >
              <CollapsibleHeader title="SII and SPI per aggregation level" class="my-5">
                <template #content>
                  <v-card style="border: 1px solid white"
                          class="pa-3 mt-3 elevation-0 rounded-xl bg-white"
                          variant="outlined">
                    <v-row>
                      <v-col cols="6">
                        <SimpleVerticalBarChart
                            class="whole-chart-border"
                            v-if="siiChartData"
                            :chartData="siiChartData"
                            :axisLabels="{ x: 'Aggregation Level', y: 'Score' }"
                            :showColorCode="false"
                            :aspectRatio="{ width: 2, height: 1 }"
                            title="Final SII Scores"
                            id="graph1"
                        >
                          <template #chart_footer>
                            <ColorCodePalette
                                :project-color-coding="projectColorCoding"
                                :projects="projects"
                            />
                          </template>
                        </SimpleVerticalBarChart>
                      </v-col>

                      <v-col cols="6">
                        <SimpleVerticalBarChart
                            class="whole-chart-border"
                            v-if="spiChartData"
                            :chartData="spiChartData"
                            :axisLabels="{ x: 'Aggregation Level', y: 'Score' }"
                            :showColorCode="false"
                            :aspectRatio="{ width: 2, height: 1 }"
                            title="Final SPI Scores"
                            id="graph2"
                        >
                          <template #chart_footer>
                            <ColorCodePalette
                                :project-color-coding="projectColorCoding"
                                :projects="projects"
                            />
                          </template>
                        </SimpleVerticalBarChart>
                      </v-col>
                    </v-row>
                  </v-card>
                </template>
              </CollapsibleHeader>

              <CollapsibleHeader title="Dimension and sector scores per aggregation level" class="mb-5">
                <template #content>
                  <v-card style="border: 1px solid white"
                          class="pa-3 mt-3 elevation-0 rounded-xl bg-white"
                          variant="outlined">
                    <v-row>
                      <v-col cols="6">
                        <RadarChartWithSelect
                            class="whole-chart-border"
                            title="Dimensions score per Aggregation Level"
                            selectPrompt="Aggregation Level:"
                            :itemsData="radarChartData"
                            id="graph3"
                        />
                      </v-col>

                      <v-col cols="6">
                        <RadarChartWithSelect
                            class="whole-chart-border"
                            title="Sectors score per Aggregation Level"
                            selectPrompt="Aggregation Level:"
                            :itemsData="sectorsRadarChartData"
                            id="graph4"
                        />
                      </v-col>
                    </v-row>
                  </v-card>
                </template>
              </CollapsibleHeader>

              <CollapsibleHeader title="Aggregation level score per dimension and sector" class="mb-5">
                <template #content>
                  <v-card style="border: 1px solid white"
                          class="pa-3 mt-3 elevation-0 rounded-xl bg-white"
                          variant="outlined">
                    <v-row>
                      <v-col cols="6">
                        <ChartWithSelect
                            class="whole-chart-border"
                            :availableItems="availableDimensions"
                            :itemsData="dimensionData"
                            :showFinalSII="false"
                            selectPrompt="Dimension:"
                            id="graph5"
                        >
                          <template #chart_footer>
                            <ColorCodePalette
                                :project-color-coding="projectColorCoding"
                                :projects="projects"
                            />
                          </template>
                        </ChartWithSelect>
                      </v-col>

                      <v-col cols="6">
                        <ChartWithSelect
                            class="whole-chart-border"
                            :availableItems="availableSectors"
                            :itemsData="sectorData"
                            :show-final-SII="false"
                            isSpiOrSii="spi"
                            selectPrompt="Sector:"
                            id="graph6"
                        >
                          <template #chart_footer>
                            <ColorCodePalette
                                :project-color-coding="projectColorCoding"
                                :projects="projects"
                            />
                          </template>
                        </ChartWithSelect>
                      </v-col>
                    </v-row>
                  </v-card>
                </template>
              </CollapsibleHeader>

              <CollapsibleHeader title="Individual KPI scores per aggregation level" class="mb-5">
                <template #content>
                  <v-card style="border: 1px solid white"
                          class="pa-3 mt-3 elevation-0 rounded-xl bg-white"
                          variant="outlined">
                    <HorizontalBarChartWithSelect
                        comparison
                        :items-data="kpisData"
                        :available-items="Object.keys(kpisData)"
                        chartType="horizontal"
                        selectPrompt="KPI:"
                        :axisLabels="{ x: 'Score', y: '' }"
                        :key="`bar_key_3`"
                        id="bar_compare_hor"
                        annotations=""
                    />
                  </v-card>
                </template>
              </CollapsibleHeader>
            </v-window-item>
            <v-window-item value="two"
                           reverse-transition="false"
                           transition="fade-transition">
              <v-col cols="12 mb-3">
                <v-card class="px-3 py-6" elevation="3">
                  <SimpleVerticalBarChart
                      v-if="callImpactData && callImpactData.labels.length > 0"
                      :chartData="callImpactData"
                      :show-color-code="false"
                      :axisLabels="{ x: 'Call Impact Id', y: 'Call Impact Score' }"
                      title="Call Impact Data"
                      id="psi1"
                  />
                </v-card>
              </v-col>
            </v-window-item>
          </v-window>
        </v-col>
      </v-row>
    </v-container>

  </MainLayout>
</template>

<script setup>
import MainLayout from "../../Layouts/Main.vue";
import SimpleVerticalBarChart from "../../Charts/simple/SimpleVerticalBarChart.vue";
import RadarChartWithSelect from "@/Charts/select/RadarChartWithSelect.vue";
import { computed, ref } from "vue";
import StringManipulation from "@/Mixins/StringManipulation";
import ChartWithSelect from "@/Charts/select/ChartWithSelect.vue";
import BackButton from "@/Shared/BackButton.vue";
import * as $routes from "@/packs/routes";
import HorizontalBarChartWithSelect from "@/Charts/select/HorizontalBarChartWithSelect.vue";
import ColorCodePalette from "@/Shared/ColorCodePalette.vue";
import CollapsibleHeader from "@/Shared/CollapsibleHeader.vue";

const props = defineProps({
  projects: {
    type: Object,
    required: true,
  },
  siiChartData: {
    type: Object,
    required: false,
  },
  spiChartData: {
    type: Object,
    required: false,
  },
  dimensionData: {
    type: Object,
    required: false,
  },
  sectorData: {
    type: Object,
    required: false,
  },
  availableDimensions: {
    type: Array,
    required: false,
  },
  availableSectors: {
    type: Array,
    default: null,
    required: false,
  },
  callImpactData: {
    type: Object,
    required: false,
  },
  callImpactGradientColors: {
    type: Array,
    required: false,
  },
  commonAggregations: {
    type: Array,
    required: false,
  },
  commonDimensions: {
    type: Array,
    required: false,
  },
  commonSectors: {
    type: Array,
    required: false,
  },
  errors: {
    type: String,
    required: false,
  },
  kpisData: {
    type: String,
    required: false
  }
});

//Handle the different tabs
const tab = ref(null);

//Pre configure the bg colors for multiple project comparison
const FRONTEND_CHART_COLORS = [
  "rgba(255, 99, 132, 0.65)",
  "rgba(54, 162, 235, 0.65)",
  "rgba(255, 205, 86, 0.65)",
  "rgba(75, 192, 192, 0.65)",
];

const projectColorCoding = computed(() => {
  return props.siiChartData.datasets.map((dataset) => dataset.backgroundColor);
});

//Handle data for radar Charts
const radarChartData = computed(() => {
  let radarDataHash = {};
  for (let dKey in props.dimensionData) {
    const dimension = props.dimensionData[dKey];
    const labels = dimension.data.labels;
    const data = dimension.data.datasets[0].data;
    const length = data.length;
    for (let i = 0; i < length; i++) {
      const label = labels[i];

      // if the key does not exist
      if (!radarDataHash[label]) {
        radarDataHash[label] = {
          labels: [],
          datasets: [
            {
              label: dimension.data.datasets[0].label,
              data: [],
              fill: true,
              backgroundColor: "rgba(255, 99, 132, 0.65)",
              borderColor: "rgb(255,99,132)",
              pointBackgroundColor: "rgb(255,99,132)",
              pointBorderColor: "#fff",
              pointHoverBackgroundColor: "#fff",
              pointHoverBorderColor: "rgb(255,99,132)",
            },
          ],
        };
      }
      for (
        let dataset = 0;
        dataset < dimension.data.datasets.length;
        dataset++
      ) {
        if (!radarDataHash[label].datasets[dataset]) {
          radarDataHash[label].datasets[dataset] = {
            label: dimension.data.datasets[dataset].label,
            data: [],
            fill: true,
            backgroundColor: FRONTEND_CHART_COLORS[dataset],
            borderColor: FRONTEND_CHART_COLORS[dataset],
            pointBackgroundColor: FRONTEND_CHART_COLORS[dataset],
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: FRONTEND_CHART_COLORS[dataset],
          };
        }
        if (dimension.data.datasets[dataset].data[i] === null) {
          radarDataHash[label].datasets[dataset].data.push(null);
        } else {
          radarDataHash[label].datasets[dataset].data.push(
            dimension.data.datasets[dataset].data[i]
          );
        }
      }
      radarDataHash[label].labels.push(
        StringManipulation.methods.clearAndCapitalize(dKey)
      );
    }
  }
  let keys = Object.keys(radarDataHash).sort();
  let sortedradarDataHash = {};
  for (let i = 0; i < keys.length; i++) {
    let key = keys[i];
    sortedradarDataHash[key] = radarDataHash[key];
  }
  return sortedradarDataHash;
});

const sectorsRadarChartData = computed(() => {
  let radarDataHash = {};
  for (let dKey in props.sectorData) {
    const sector = props.sectorData[dKey];
    const labels = sector.data.labels;
    const data = sector.data.datasets[0].data;
    const data2 = sector.data.datasets[1].data;
    const length = data.length;

    for (let i = 0; i < length; i++) {
      const label = labels[i];

      // if the key does not exist
      if (!radarDataHash[label]) {
        radarDataHash[label] = {
          labels: [],
          datasets: [
            {
              label: sector.data.datasets[0].label,
              data: [],
              fill: false,
              backgroundColor: "rgba(255, 99, 132, 0.65)",
              borderColor: "rgb(255,99,132)",
              pointBackgroundColor: "rgb(255,99,132)",
              pointBorderColor: "#fff",
              pointHoverBackgroundColor: "#fff",
              pointHoverBorderColor: "rgb(255,99,132)",
            },
          ],
        };
      }
      for (let dataset = 0; dataset < sector.data.datasets.length; dataset++) {
        if (!radarDataHash[label].datasets[dataset]) {
          radarDataHash[label].datasets[dataset] = {
            label: sector.data.datasets[dataset].label,
            data: [],
            fill: true,
            backgroundColor: "rgba(54, 162, 235, 0.65)",
            borderColor: "rgb(54,162,235)",
            pointBackgroundColor: "rgb(54,162,235)",
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: "rgb(54,162,235)",
          };
        }
        radarDataHash[label].datasets[dataset].data.push(
          sector.data.datasets[dataset].data[i]
        );
      }
      radarDataHash[label].labels.push(
        StringManipulation.methods.clearAndCapitalize(dKey)
      );
    }
  }
  let keys = Object.keys(radarDataHash).sort();
  let sortedradarDataHash = {};
  for (let i = 0; i < keys.length; i++) {
    let key = keys[i];
    sortedradarDataHash[key] = radarDataHash[key];
  }
  return sortedradarDataHash;
});
</script>

<style scoped>

.whole-chart-border {
  border: solid 1px #999999;
  padding: 15px;
}

</style>
